import useSWR, { useSWRConfig } from "swr";
import useSWRInfinite from "swr/infinite";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { AttributeFilters } from "@/models/attributeFilter";
import { Conversation, Message, MessageType, TMP_CONVERSATION_KEY } from "@/models/conversation";
import { defaultSWROption } from "@/services/api/api.service";
import { ConversationService } from "@/services/conversation/conversation.service";
import { initialConversationState, useChatStore } from "@/stores/ui/useChatStore";
import { showError } from "@/utils/notification";

/** 未確定会話を表す一意キー */
const CONVERSATION_PAGE_LIMIT = 10;

const createOptimisticUserMessage = (
  content: string,
  categories: string[] = [],
  vocPeriodStart?: Date | null,
  vocPeriodEnd?: Date | null,
  attributeFilters?: AttributeFilters,
  tagIds?: string[],
): Message => {
  const userMessageId = `temp-user-${Date.now()}`;
  return new Message(
    userMessageId,
    MessageType.USER,
    content,
    undefined,
    categories,
    vocPeriodStart || undefined,
    vocPeriodEnd || undefined,
    attributeFilters,
    tagIds,
  );
};

export const CONVERSATION_SWR_KEY = "conversations";
export const MESSAGES_SWR_KEY = "conversation-messages";

export const useConversation = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { mutate } = useSWRConfig();
  const currentConversationId = useChatStore((s) => s.currentConversationId);
  const conversationStates = useChatStore((s) => s.conversationStates);
  const setCurrentConversationId = useChatStore((s) => s.setCurrentConversationId);
  const setConversationState = useChatStore((s) => s.setConversationState);

  const selectedMessageId = conversationStates[currentConversationId]?.selectedMessageId;

  // 会話リスト（useSWRInfinite をフック内で管理）
  const getConvKey = (
    pageIndex: number,
    previousPageData: { conversations: Conversation[]; hasNext: boolean; } | null,
  ) => {
    if (!sessionCurrentWorkspaceId) return null;
    if (previousPageData && !previousPageData.hasNext) return null;
    const offset = pageIndex * CONVERSATION_PAGE_LIMIT;
    return [CONVERSATION_SWR_KEY, sessionCurrentWorkspaceId, offset, CONVERSATION_PAGE_LIMIT];
  };

  const convFetcher = ([, workspaceId, offset, limit]: [
    typeof CONVERSATION_SWR_KEY,
    string,
    number,
    number,
  ]) => ConversationService.fetchConversations(workspaceId, offset, limit);

  const {
    data: convPages,
    error: conversationsError,
    isLoading: isLoadingConversations,
    size: conversationsPageSize,
    setSize: setConversationsPageSize,
    mutate: mutateConversations,
  } = useSWRInfinite(getConvKey, convFetcher, defaultSWROption);
  /** 会話リストのページング */
  const loadMoreConversations = () => {
    if (!isLoadingConversations && hasMoreConversations) {
      setConversationsPageSize(conversationsPageSize + 1);
    }
  };
  const conversations: Conversation[] | undefined = convPages?.flatMap((page) => page.conversations);

  const hasMoreConversations = convPages?.at(-1)?.hasNext ?? false;

  // SWRでメッセージを取得（useConversation 内で使用）
  const shouldFetchMessages = !!currentConversationId && currentConversationId !== TMP_CONVERSATION_KEY;
  const { data: messages, error: messagesError, isLoading: isLoadingMessages } = useSWR(
    [MESSAGES_SWR_KEY, currentConversationId],
    shouldFetchMessages ? ([, id]) => ConversationService.fetchMessages(id) : null,
    defaultSWROption,
  );

  /** 新規会話の作成（未確定状態は TMP_CONVERSATION_KEY に積み上げ→成功後に移行） */
  const createConversation = async (
    content: string,
    categories: string[] = [],
    vocPeriodStart?: Date | null,
    vocPeriodEnd?: Date | null,
    onSuccess?: (conversationId: string) => void,
    attributeFilters?: AttributeFilters,
    tagIds?: string[],
  ): Promise<{ conversationId: string; messages: Message[]; }> => {
    if (!sessionCurrentWorkspaceId) throw new Error("No workspace selected");

    setConversationState(TMP_CONVERSATION_KEY, { isWaitingForAssistantMessage: true });

    // TMP_CONVERSATION_KEY に楽観反映
    const userMessage = createOptimisticUserMessage(
      content,
      categories,
      vocPeriodStart,
      vocPeriodEnd,
      attributeFilters,
      tagIds,
    );
    mutate([MESSAGES_SWR_KEY, TMP_CONVERSATION_KEY], [userMessage], false);

    try {
      const result = await ConversationService.createConversation(
        sessionCurrentWorkspaceId,
        content,
        categories,
        vocPeriodStart,
        vocPeriodEnd,
        attributeFilters,
        tagIds,
      );

      const newConversationId = result.conversation.id;
      mutate([MESSAGES_SWR_KEY, newConversationId], result.messages, false); // 新規会話に結果をセット
      setCurrentConversationId(newConversationId);
      mutateConversations(); // リストを更新
      if (onSuccess) onSuccess(newConversationId);
      return { conversationId: newConversationId, messages: result.messages };
    } catch (error) {
      console.error(error);
      showError("会話の作成に失敗しました。");
      throw error;
    } finally {
      mutate([MESSAGES_SWR_KEY, TMP_CONVERSATION_KEY], undefined, false); // 楽観をクリア
      setConversationState(TMP_CONVERSATION_KEY, initialConversationState);
    }
  };

  /** 既存会話への投稿（ID未確定ならエラーにして呼び出し側で createConversation を使う想定） */
  const postMessage = async (
    content: string,
    categories: string[] = [],
    vocPeriodStart?: Date | null,
    vocPeriodEnd?: Date | null,
    onSuccess?: (conversationId: string) => void,
    attributeFilters?: AttributeFilters,
    tagIds?: string[],
  ): Promise<Message[]> => {
    const id = currentConversationId;
    if (!id) {
      throw new Error("No conversation selected");
    }
    setConversationState(id, { isWaitingForAssistantMessage: true });
    const userMessage = createOptimisticUserMessage(
      content,
      categories,
      vocPeriodStart,
      vocPeriodEnd,
      attributeFilters,
      tagIds,
    );
    mutate([MESSAGES_SWR_KEY, id], (msgs: Message[] | undefined) => {
      if (!msgs) return [userMessage];
      return [userMessage, ...msgs];
    }, false);

    try {
      const newMessages = await ConversationService.postMessage(
        id,
        content,
        categories,
        vocPeriodStart,
        vocPeriodEnd,
        attributeFilters,
        tagIds,
      );

      mutate([MESSAGES_SWR_KEY, id], (msgs: Message[] | undefined) => {
        if (!msgs) return newMessages;
        const filtered = msgs.filter((m) => !m.id.startsWith("temp-"));
        return [...newMessages, ...filtered];
      }, false);

      if (onSuccess) onSuccess(id);
      return newMessages;
    } catch (error) {
      console.error(error);
      showError("メッセージの送信に失敗しました。");
      mutate([MESSAGES_SWR_KEY, id], (msgs: Message[] | undefined) => {
        if (!msgs) return [];
        return msgs.filter((m) => !m.id.startsWith("temp-"));
      }, false);
      throw error;
    } finally {
      setConversationState(id, { isWaitingForAssistantMessage: false });
    }
  };

  // 選択されたASSISTANTメッセージに対応するUSERメッセージを取得
  let correspondingUserMessage: Message | null = null;
  if (selectedMessageId && messages) {
    const selectedIndex = messages.findIndex((m) => m.id === selectedMessageId);
    if (selectedIndex >= 0 && selectedIndex < messages.length - 1) {
      const nextMessage = messages[selectedIndex + 1];
      if (nextMessage.type === MessageType.USER) {
        correspondingUserMessage = nextMessage;
      }
    }
  }

  return {
    conversations,
    messages,
    currentConversationId,
    isLoadingConversations,
    hasMoreConversations,
    isLoadingMessages,
    conversationsError,
    messagesError,
    createConversation,
    postMessage,
    setCurrentConversationId,
    loadMoreConversations,
    selectedMessageId,
    correspondingUserMessage,
  };
};
